import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Mail, ToggleLeft, ToggleRight, Shield, ShoppingBag, Star, FileText, UserPlus, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORY_ICONS = {
  'Inscription': UserPlus,
  'Commandes': ShoppingBag,
  'Marketing': Star,
  'Sécurité': Shield,
  'Rapports': FileText,
  'Onboarding': UserPlus,
  'Admin': Shield,
};

export default function EmailSettings() {
  useAuth();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/email-settings`);
      setSettings(res.data.settings);
    } catch {
      toast.error('Erreur lors du chargement des parametres');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const toggleEmail = async (emailType) => {
    setToggling(emailType);
    try {
      const res = await axios.put(`${API}/admin/email-settings/${emailType}`, {});
      setSettings(prev => prev.map(s => 
        s.email_type === emailType ? { ...s, enabled: res.data.enabled } : s
      ));
      toast.success(`${res.data.label}: ${res.data.enabled ? 'active' : 'desactive'}`);
    } catch {
      toast.error('Erreur lors de la modification');
    } finally {
      setToggling(null);
    }
  };

  const toggleAll = async () => {
    setToggling('all');
    try {
      const res = await axios.put(`${API}/admin/email-settings-bulk`, {});
      setSettings(prev => prev.map(s => ({ ...s, enabled: res.data.enabled })));
      toast.success(res.data.enabled ? 'Tous les emails actives' : 'Tous les emails desactives');
    } catch {
      toast.error('Erreur');
    } finally {
      setToggling(null);
    }
  };

  const grouped = settings.reduce((acc, s) => {
    if (!acc[s.category]) acc[s.category] = [];
    acc[s.category].push(s);
    return acc;
  }, {});

  const allEnabled = settings.length > 0 && settings.every(s => s.enabled);
  const enabledCount = settings.filter(s => s.enabled).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Controle des emails</h1>
          <p className="text-sm text-gray-500 mt-1">
            {enabledCount}/{settings.length} types d'emails actifs
          </p>
        </div>
        <button
          onClick={toggleAll}
          disabled={toggling === 'all'}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors hover:bg-gray-50"
          data-testid="toggle-all-emails"
        >
          {toggling === 'all' ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : allEnabled ? (
            <ToggleRight className="w-4 h-4 text-emerald-600" />
          ) : (
            <ToggleLeft className="w-4 h-4 text-gray-400" />
          )}
          {allEnabled ? 'Tout desactiver' : 'Tout activer'}
        </button>
      </div>

      {Object.entries(grouped).map(([category, items]) => {
        const Icon = CATEGORY_ICONS[category] || Mail;
        return (
          <Card key={category} className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Icon className="w-5 h-5 text-emerald-600" />
              <h2 className="font-semibold text-gray-800">{category}</h2>
              <span className="text-xs text-gray-400 ml-auto">
                {items.filter(i => i.enabled).length}/{items.length} actifs
              </span>
            </div>
            <div className="space-y-3">
              {items.map(item => (
                <div
                  key={item.email_type}
                  className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 transition-colors"
                  data-testid={`email-setting-${item.email_type}`}
                >
                  <div className="flex items-center gap-3">
                    <Mail className={`w-4 h-4 ${item.enabled ? 'text-emerald-500' : 'text-gray-300'}`} />
                    <div>
                      <p className={`text-sm font-medium ${item.enabled ? 'text-gray-900' : 'text-gray-400'}`}>
                        {item.label}
                      </p>
                      {item.updated_at && (
                        <p className="text-xs text-gray-400">
                          Modifie le {new Date(item.updated_at).toLocaleDateString('fr-FR')}
                        </p>
                      )}
                    </div>
                  </div>
                  <Switch
                    checked={item.enabled}
                    onCheckedChange={() => toggleEmail(item.email_type)}
                    disabled={toggling === item.email_type}
                    data-testid={`toggle-${item.email_type}`}
                  />
                </div>
              ))}
            </div>
          </Card>
        );
      })}
    </div>
  );
}
